import React, { useState, useEffect, useRef } from 'react';
import api from '../api/client';
import { currencySymbol } from '../utils/orderUnits';
import './AddOrderPopup_SmallBags.css';
import './CreateOrderPopup.css';

// Типы материалов, у которых есть доп. расходы и настройки поставщика в заказе (вкладки страницы)
const TYPES_WITH_SETTINGS = new Set([1, 2, 3, 6]);

const CURRENT_YEAR  = new Date().getFullYear();
const CURRENT_MONTH = new Date().getMonth() + 1;

const MONTHS = [
  { num: 1, label: 'Jan' }, { num: 2, label: 'Feb' }, { num: 3, label: 'Mar' },
  { num: 4, label: 'Apr' }, { num: 5, label: 'May' }, { num: 6, label: 'Jun' },
  { num: 7, label: 'Jul' }, { num: 8, label: 'Aug' }, { num: 9, label: 'Sep' },
  { num: 10, label: 'Oct' }, { num: 11, label: 'Nov' }, { num: 12, label: 'Dec' },
];

/**
 * CreateOrderPopup
 * Lets the user create a new order (material or seeds) with multiple items at once.
 *
 * Props:
 *   materialTypes   [{materialtypeID, materialName}]  (does NOT include Seeds — added internally)
 *   statuses        [{statusID, status_name}]
 *   seedsSuppliers  [{supplierID, supplierName}]
 *   onClose
 *   onSuccess
 */
const CreateOrderPopup = ({ materialTypes = [], statuses = [], seedsSuppliers = [], onClose, onSuccess }) => {
  // ── Header fields ────────────────────────────────────────────────────────────
  const [orderID,       setOrderID]       = useState('');
  const [typeKey,       setTypeKey]       = useState('');   // 'seeds' or String(materialtypeID)
  const [year,          setYear]          = useState(CURRENT_YEAR);
  const [month,         setMonth]         = useState(CURRENT_MONTH);
  const [supplierID,    setSupplierID]    = useState('');
  const [statusID,      setStatusID]      = useState('');
  const [estDelDate,    setEstDelDate]    = useState('');
  const [boFollowUpDate, setBoFollowUpDate] = useState('');
  const [tracking,      setTracking]      = useState('');

  // ── Валюта, курс, единица веса, Import order — настройки поставщика в заказе ──
  // (order_extra_costs); уходят на сервер вместе с первой позицией, канон lb / USD считает сервер
  const [currency,   setCurrency]   = useState('USD');
  const [fxRate,     setFxRate]     = useState('');
  const [weightUnit, setWeightUnit] = useState('lb');
  const [isImport,   setIsImport]   = useState(false);

  // ── Suppliers for the selected type ─────────────────────────────────────────
  const [suppliers, setSuppliers] = useState([]);

  // ── Items ────────────────────────────────────────────────────────────────────
  // Each item: { itemID, itemName, qty, price }
  const [items, setItems] = useState([]);

  // ── Product / seeds search ───────────────────────────────────────────────────
  const [itemSearch,    setItemSearch]    = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [searchLoading, setSearchLoading] = useState(false);
  const [showDropdown,  setShowDropdown]  = useState(false);
  const searchRef   = useRef(null);
  const timerRef    = useRef(null);
  const listRef     = useRef(null);

  const [isSaving, setIsSaving] = useState(false);
  const [saveError, setSaveError] = useState('');

  const isSeeds = typeKey === 'seeds';
  const isBO    = statusID === 'BO';
  const matTypeID = isSeeds ? null : (typeKey ? parseInt(typeKey) : null);
  const hasSettings = isSeeds || TYPES_WITH_SETTINGS.has(matTypeID);
  const isEUR   = hasSettings && currency === 'EUR';
  const sym     = currencySymbol(hasSettings ? currency : 'USD');
  const uLabel  = isSeeds ? weightUnit : 'pcs';

  const handleCurrencyChange = async (cur) => {
    setCurrency(cur);
    if (cur === 'EUR' && !(parseFloat(fxRate) > 0)) {
      // Подстановка курса ECB на сегодня; правится руками
      try { const { data } = await api.get('/api/material-orders/fx-rate'); setFxRate(String(data.rate)); }
      catch (_) { /* источник недоступен — курс вводится вручную */ }
    }
  };

  // ── Type options shown in select ─────────────────────────────────────────────
  const typeOptions = [
    ...materialTypes.map(t => ({ value: String(t.materialtypeID), label: t.materialName })),
    { value: 'seeds', label: 'Seeds' },
  ];

  // ── Reload suppliers when type changes ───────────────────────────────────────
  useEffect(() => {
    setSupplierID('');
    setItems([]);
    setItemSearch('');
    setSearchResults([]);
    setShowDropdown(false);
    setWeightUnit('lb');

    if (!typeKey) { setSuppliers([]); return; }

    if (isSeeds) {
      setSuppliers(seedsSuppliers);
    } else {
      api.get(`/api/materialsuppliers_for_type/${typeKey}`)
        .then(({ data }) => setSuppliers(Array.isArray(data) ? data : []))
        .catch(() => setSuppliers([]));
    }
  }, [typeKey]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Debounced product/seeds search ──────────────────────────────────────────
  useEffect(() => {
    if (!itemSearch.trim() || !typeKey) {
      setSearchResults([]);
      return;
    }
    clearTimeout(timerRef.current);
    timerRef.current = setTimeout(async () => {
      setSearchLoading(true);
      try {
        const q = encodeURIComponent(itemSearch.trim());
        const url = isSeeds
          ? `/api/material-orders/search-seeds?search=${q}`
          : `/api/material-orders/search-products?materialtypeID=${typeKey}&search=${q}`;
        const { data } = await api.get(url);
        setSearchResults(Array.isArray(data) ? data : []);
        setShowDropdown(true);
      } catch (err) {
        setSearchResults([]);
        if (err.response) setShowDropdown(true); // HTTP error: fetch showed the "No results" dropdown
      }
      setSearchLoading(false);
    }, 280);
  }, [itemSearch, typeKey, isSeeds]);

  // ── Close dropdown on outside click ─────────────────────────────────────────
  useEffect(() => {
    const handler = (e) => {
      if (searchRef.current && !searchRef.current.contains(e.target)) {
        setShowDropdown(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  // ── Item helpers ─────────────────────────────────────────────────────────────
  const handleAddItem = (result) => {
    const id = isSeeds ? result.seedsID : result.sku;
    if (items.some(i => i.itemID === id)) {
      setItemSearch('');
      setShowDropdown(false);
      return;
    }
    // Новая позиция — сверху списка, сразу под поиском: количество и цену не нужно искать прокруткой
    setItems(prev => [{ itemID: id, itemName: result.name, qty: '', price: '' }, ...prev]);
    setItemSearch('');
    setSearchResults([]);
    setShowDropdown(false);
    if (listRef.current) listRef.current.scrollTop = 0;
  };

  const handleRemoveItem = (itemID) =>
    setItems(prev => prev.filter(i => i.itemID !== itemID));

  const handleItemChange = (itemID, field, value) =>
    setItems(prev => prev.map(i => i.itemID === itemID ? { ...i, [field]: value } : i));

  // ── Total ────────────────────────────────────────────────────────────────────
  const orderTotal = items.reduce((sum, item) => {
    const q = parseFloat(item.qty);
    const p = parseFloat(item.price);
    return sum + ((!isNaN(q) && !isNaN(p)) ? q * p : 0);
  }, 0);

  // ── Save ─────────────────────────────────────────────────────────────────────
  const handleSave = async () => {
    setSaveError('');
    if (!typeKey)      return setSaveError('Please select a material type.');
    if (!supplierID)   return setSaveError('Please select a supplier.');
    if (!statusID)     return setSaveError('Please select a status.');
    if (!isBO && !estDelDate)    return setSaveError('Please fill Estimated Delivery Date.');
    if (isBO  && !boFollowUpDate) return setSaveError('Please fill Backorder Follow-up Date.');
    if (items.length === 0) return setSaveError('Please add at least one item.');
    if (isEUR && !(parseFloat(fxRate) > 0)) return setSaveError('Please enter the EUR→USD rate.');

    for (const item of items) {
      const q = parseFloat(item.qty);
      const p = parseFloat(item.price);
      if (!item.qty || isNaN(q) || q <= 0)
        return setSaveError(`Please enter a valid quantity for "${item.itemName}".`);
      if (!item.price || isNaN(p) || p <= 0)
        return setSaveError(`Please enter a valid price for "${item.itemName}".`);
    }

    setIsSaving(true);
    try {
      const realOrderID = orderID.trim() || null;
      // Настройки поставщика в заказе — с первой позицией; количество и цена — как введены
      const settings = hasSettings ? {
        currency,
        fx_rate:     isEUR ? parseFloat(fxRate) : null,
        weight_unit: isSeeds ? weightUnit : 'lb',
        is_import:   isImport ? 1 : 0,
      } : null;
      // Список показывает новые позиции сверху; сохраняем в порядке добавления
      for (const [idx, item] of [...items].reverse().entries()) {
        const body = {
          isSeeds,
          orderID: realOrderID,
          year: parseInt(year),
          month_num: parseInt(month),
          supplierID: parseInt(supplierID),
          statusID,
          est_del_date:      isBO ? null : estDelDate,
          bo_follow_up_date: isBO ? boFollowUpDate : null,
          tracking: tracking.trim() || null,
          price_entered: parseFloat(item.price),
          ...(isSeeds
            ? { seedsID: item.itemID, qty_entered: parseFloat(item.qty) }
            : { sku: item.itemID, qty: parseInt(item.qty), materialtypeID: matTypeID }),
          ...(idx === 0 && settings ? { settings } : {}),
        };
        try {
          await api.post('/api/material-orders/add-item', body);
        } catch (e) {
          if (e.response) throw new Error(e.response.data?.error || 'Server error');
          throw e;
        }
      }
      onSuccess?.();
      onClose();
    } catch (err) {
      setSaveError(err.message || 'Failed to save order. Please try again.');
    } finally {
      setIsSaving(false);
    }
  };

  // ── Render ───────────────────────────────────────────────────────────────────
  return (
    <div className="sb-popup-overlay">
      <div className="sb-popup-modal co-modal">

        {/* Header */}
        <div className="sb-popup-header">
          <h2 className="sb-popup-title">
            <i className="fa-solid fa-cart-plus" style={{ marginRight: 8 }}></i>
            Create New Order
          </h2>
          <p className="sb-popup-subtitle"></p>
        </div>

        {/* Body */}
        <div className="sb-popup-body co-body">

          {/* Row 1: Order # + Type */}
          <div className="sb-form-row">
            <div className="sb-form-group">
              <label className="sb-label">
                Order # <span style={{ color: '#94a3b8', fontWeight: 400, textTransform: 'none' }}>(optional)</span>
              </label>
              <input
                className="sb-input"
                type="text"
                value={orderID}
                onChange={e => setOrderID(e.target.value)}
                placeholder="e.g. PO-2025-001"
              />
            </div>
            <div className="sb-form-group">
              <label className="sb-label">Material Type <span className="required">*</span></label>
              <select className="sb-select" value={typeKey} onChange={e => setTypeKey(e.target.value)}>
                <option value="">Select type</option>
                {typeOptions.map(t => (
                  <option key={t.value} value={t.value}>{t.label}</option>
                ))}
              </select>
            </div>
          </div>

          {/* Row 2: Supplier + Period */}
          <div className="sb-form-row">
            <div className="sb-form-group">
              <label className="sb-label">Supplier <span className="required">*</span></label>
              <select
                className="sb-select"
                value={supplierID}
                onChange={e => setSupplierID(e.target.value)}
                disabled={!typeKey}
              >
                <option value="">Select supplier</option>
                {suppliers.map(s => (
                  <option key={s.supplierID} value={s.supplierID}>{s.supplierName}</option>
                ))}
              </select>
            </div>
            <div className="sb-form-group">
              <label className="sb-label">Period <span className="required">*</span></label>
              <div className="co-period-row">
                <input
                  className="sb-input co-year-input"
                  type="number"
                  value={year}
                  onChange={e => setYear(e.target.value)}
                  min="2020"
                  max="2035"
                />
                <span className="co-period-sep">/</span>
                <select
                  className="sb-select"
                  value={month}
                  onChange={e => setMonth(e.target.value)}
                >
                  {MONTHS.map(m => (
                    <option key={m.num} value={m.num}>{m.label}</option>
                  ))}
                </select>
              </div>
            </div>
          </div>

          {/* Row 3: Status + Date */}
          <div className="sb-form-row">
            <div className="sb-form-group">
              <label className="sb-label">Status <span className="required">*</span></label>
              <select className="sb-select" value={statusID} onChange={e => setStatusID(e.target.value)}>
                <option value="">Select status</option>
                {statuses.map(s => (
                  <option key={s.statusID} value={s.statusID}>{s.status_name}</option>
                ))}
              </select>
            </div>
            <div className="sb-form-group">
              {!isBO ? (
                <>
                  <label className="sb-label">Est. Delivery Date <span className="required">*</span></label>
                  <input
                    className="sb-input"
                    type="date"
                    value={estDelDate}
                    onChange={e => setEstDelDate(e.target.value)}
                  />
                </>
              ) : (
                <>
                  <label className="sb-label">BO Follow-up Date <span className="required">*</span></label>
                  <input
                    className="sb-input"
                    type="date"
                    value={boFollowUpDate}
                    onChange={e => setBoFollowUpDate(e.target.value)}
                  />
                </>
              )}
            </div>
          </div>

          {/* Tracking */}
          <div className="sb-form-group">
            <label className="sb-label">
              Tracking <span style={{ color: '#94a3b8', fontWeight: 400, textTransform: 'none' }}>(optional)</span>
            </label>
            <input
              className="sb-input"
              type="text"
              value={tracking}
              onChange={e => setTracking(e.target.value)}
              placeholder="Optional"
            />
          </div>

          {/* Валюта поставщика, курс, единица веса, Import order */}
          {hasSettings && (
            <div className="sb-form-row">
              <div className="sb-form-group">
                <label className="sb-label">Currency</label>
                <div className="co-period-row">
                  <select className="sb-select" value={currency} onChange={e => handleCurrencyChange(e.target.value)}>
                    <option value="USD">USD</option>
                    <option value="EUR">EUR</option>
                  </select>
                  {isEUR && (
                    <input
                      className="sb-input"
                      type="number"
                      step="0.0001"
                      min="0"
                      placeholder="Rate EUR→USD"
                      title="ECB rate for today is prefilled; edit if the invoice used another rate"
                      value={fxRate}
                      onChange={e => setFxRate(e.target.value)}
                    />
                  )}
                </div>
              </div>
              <div className="sb-form-group">
                <label className="sb-label">{isSeeds ? 'Unit & import' : 'Import'}</label>
                <div className="co-period-row" style={{ alignItems: 'center', gap: 12 }}>
                  {isSeeds && (
                    <select className="sb-select" value={weightUnit} onChange={e => setWeightUnit(e.target.value)} style={{ maxWidth: 90 }}>
                      <option value="lb">lb</option>
                      <option value="kg">kg</option>
                    </select>
                  )}
                  <label style={{ display: 'flex', alignItems: 'center', gap: 6, cursor: 'pointer', fontSize: '0.85rem', textTransform: 'none' }}>
                    <input type="checkbox" checked={isImport} onChange={e => setIsImport(e.target.checked)} />
                    Import order
                  </label>
                </div>
              </div>
            </div>
          )}

          <hr className="sb-divider" />

          {/* Items section */}
          <div className="co-items-section">
            <div className="co-items-header">
              <span className="sb-label">Items</span>
              {items.length > 0 && (
                <span className="co-items-count">
                  {items.length} item{items.length !== 1 ? 's' : ''}
                </span>
              )}
            </div>

            {/* Search input + dropdown */}
            <div className="co-search-wrapper" ref={searchRef}>
              <input
                className="sb-input"
                type="text"
                placeholder={
                  !typeKey
                    ? 'Select a type first'
                    : `Search ${isSeeds ? 'seeds' : 'products'}…`
                }
                value={itemSearch}
                onChange={e => { setItemSearch(e.target.value); if (e.target.value) setShowDropdown(true); }}
                onFocus={() => { if (searchResults.length > 0) setShowDropdown(true); }}
                disabled={!typeKey}
              />
              {showDropdown && (searchLoading || searchResults.length > 0 || (itemSearch && !searchLoading)) && (
                <div className="co-search-dropdown">
                  {searchLoading ? (
                    <div className="co-search-loading">
                      <i className="fa-solid fa-spinner fa-spin" style={{ marginRight: 6 }}></i>
                      Searching…
                    </div>
                  ) : searchResults.length > 0 ? (
                    searchResults.map(r => {
                      const id = isSeeds ? r.seedsID : r.sku;
                      const alreadyAdded = items.some(i => i.itemID === id);
                      return (
                        <button
                          key={id}
                          className={`co-search-item${alreadyAdded ? ' co-search-item--added' : ''}`}
                          onClick={() => handleAddItem(r)}
                          disabled={alreadyAdded}
                        >
                          <span className="co-search-item-name">{r.name}</span>
                          <span className="co-search-item-id">{isSeeds ? `#${id}` : id}</span>
                          {alreadyAdded && <span className="co-search-item-badge">Added</span>}
                        </button>
                      );
                    })
                  ) : (
                    <div className="co-search-empty">No results for "{itemSearch}"</div>
                  )}
                </div>
              )}
            </div>

            {/* Items list */}
            {items.length > 0 && (
              <div className="co-items-list" ref={listRef}>
                {items.map(item => {
                  const q = parseFloat(item.qty);
                  const p = parseFloat(item.price);
                  const subtotal = (!isNaN(q) && !isNaN(p)) ? (q * p).toFixed(2) : '0.00';
                  return (
                    <div key={item.itemID} className="co-item-row">
                      <div style={{ display: 'flex', alignItems: 'baseline', gap: 8 }}>
                        <span className="co-item-name">{item.itemName}</span>
                        <span className="co-item-id">{isSeeds ? `#${item.itemID}` : item.itemID}</span>
                      </div>
                      <div className="co-item-fields">
                        <div className="co-item-field">
                          <label className="co-item-label">Qty ({uLabel})</label>
                          <input
                            className="sb-input co-item-input"
                            type="number"
                            step={isSeeds ? '0.01' : '1'}
                            min="0"
                            placeholder="0"
                            value={item.qty}
                            onChange={e => handleItemChange(item.itemID, 'qty', e.target.value)}
                          />
                        </div>
                        <div className="co-item-field">
                          <label className="co-item-label">Price / {uLabel} ({sym})</label>
                          <input
                            className="sb-input co-item-input"
                            type="number"
                            step="0.0001"
                            min="0"
                            placeholder="0.00"
                            value={item.price}
                            onChange={e => handleItemChange(item.itemID, 'price', e.target.value)}
                          />
                        </div>
                        <div className="co-item-field co-item-field--subtotal">
                          <label className="co-item-label">Subtotal</label>
                          <span className="co-item-subtotal">{sym}{subtotal}</span>
                        </div>
                        <button
                          className="co-item-remove"
                          onClick={() => handleRemoveItem(item.itemID)}
                          title="Remove item"
                        >
                          <i className="fa-solid fa-xmark"></i>
                        </button>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}

            {/* Order total */}
            {items.length > 0 && (
              <div className="sb-total-banner">
                <span className="sb-total-label">
                  <i className="fa-solid fa-calculator" style={{ marginRight: 6 }}></i>
                  Order Total
                </span>
                <span className="sb-total-value">
                  {sym}{orderTotal.toFixed(2)}
                  {isEUR && parseFloat(fxRate) > 0 && (
                    <span style={{ fontWeight: 400, opacity: 0.8, marginLeft: 6 }}>≈ ${(orderTotal * parseFloat(fxRate)).toFixed(2)}</span>
                  )}
                </span>
              </div>
            )}

            {/* Empty hint */}
            {items.length === 0 && typeKey && (
              <div className="co-empty-hint">
                <i className="fa-solid fa-magnifying-glass" style={{ marginRight: 6 }}></i>
                Search above to add items to this order
              </div>
            )}
          </div>

          {/* Error message */}
          {saveError && (
            <div style={{
              background: '#fef2f2', border: '1px solid #fca5a5', borderRadius: 7,
              padding: '9px 14px', color: '#b91c1c', fontSize: '0.85rem',
            }}>
              <i className="fa-solid fa-circle-exclamation" style={{ marginRight: 6 }}></i>
              {saveError}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="sb-popup-footer">
          <button className="sb-btn sb-btn-cancel" onClick={onClose}>Cancel</button>
          <button
            className="sb-btn sb-btn-save"
            onClick={handleSave}
            disabled={isSaving}
          >
            {isSaving
              ? <><i className="fa-solid fa-spinner fa-spin" style={{ marginRight: 6 }}></i>Saving…</>
              : <><i className="fa-solid fa-floppy-disk" style={{ marginRight: 6 }}></i>
                  Save Order{items.length > 0 ? ` (${items.length})` : ''}</>
            }
          </button>
        </div>

      </div>
    </div>
  );
};

export default CreateOrderPopup;
